import $, { Cash } from 'cash-dom';

/**
 * Newsletter Module
 * Handles the newsletter subscription via Mailchimp
 * @package KAA
 * @author Tim Jungmann <Tim@Humans-Machines.com>
 */
export default class Newsletter {
    /**
     * Singleton instance of the component
     */
    static #instance: Newsletter;

    /**
     * Cash body reference
     */
    protected $body: Cash;

    /**
     * UI references
     */
    protected ui = {
		newsletter: '.js-newsletter',
        subscribeForm: '.js-subscribe-form',
        successMessage: '.js-newsletter-success',
        errorMessage: '#mce-error-response',
    };

    /**
     * Newsletter constructor
     */
    protected constructor() {
        this.$body = $('body');
        this.registerEvents();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Newsletter {
        if (!Newsletter.#instance) {
            Newsletter.#instance = new Newsletter();
        }
        return Newsletter.#instance;
    }

    /**
     * Register required DOM events
     */
    protected registerEvents(): void {
        this.$body.on('submit', this.ui.subscribeForm, (event: Event) => {
            event.preventDefault();
            this.newsletterSubscribe();
        });
    }

    /**
     * Handle the newsletter subscription
     */
    protected newsletterSubscribe(): void {
        const $form = $(this.ui.subscribeForm);
        // Add a loading class to indicate processing
        $form.addClass('is-loading');

        // Process the subscription via Mailchimp JSONP
        this.processNewsletterSubscription($form);
    }

    /**
     * Process the subscription using JSONP by dynamically injecting a script tag.
     * @param $form The form element wrapped by Cash
     */
    protected processNewsletterSubscription($form: Cash): void {
        const formData = $form.serialize();
        let actionUrl = $form.attr('action') || '';

        // Convert the action URL to Mailchimp's JSONP endpoint
        actionUrl = actionUrl.replace('/post?', '/post-json?');

        // Define a unique callback name
        const callbackName = 'jsonpCallback_' + Date.now();
        // Append the callback parameter to the URL
        const separator = actionUrl.indexOf('?') !== -1 ? '&' : '?';
        actionUrl += `${separator}c=${callbackName}`;

        // Append the serialized form data
        const finalUrl = actionUrl + '&' + formData;

        // Create a script element to perform the JSONP request
        const script = document.createElement('script');
        script.src = finalUrl;

        // Define the callback function to handle the JSONP response
        (window as any)[callbackName] = (data: any) => {
            // Cleanup: remove the script tag and delete the callback
            document.body.removeChild(script);
            delete (window as any)[callbackName];

            // Remove the loading indicator
            $form.removeClass('is-loading');

            if (data.result === "success") {
				$form.hide();
				// Add the "was-successful" class to the newsletter container
				$(this.ui.newsletter).addClass('was-successful');
			} else {
				// Display the error message from Mailchimp
				$(this.ui.errorMessage).html(data.msg).show();
			}
        };

        // Append the script tag to the document to initiate the request
        document.body.appendChild(script);
    }
}
